import { searchManager } from "./search/search-manager";
import { webSearcher } from "./search/web-searcher";
import { chatSearcher } from "./search/chat-searcher";
import type {
  SearchResult,
  SearchResultSection,
  SearcherRegistration,
} from "@pineseed/gui/types";

/**
 * Search Handler
 * STRICT RULE: Contains ONLY business logic - no UI imports, no direct atom mutations
 * Bridges search manager with state atoms following handler pattern
 */

export class SearchHandler {
  private static instance: SearchHandler;
  private searchTimeout: number | null = null;
  private readonly SEARCH_DEBOUNCE_MS = 300;

  public static getInstance(): SearchHandler {
    if (!SearchHandler.instance) {
      SearchHandler.instance = new SearchHandler();
      // Initialize searchers on first instantiation
      SearchHandler.instance.initializeSearchers();
    }
    return SearchHandler.instance;
  }

  /**
   * Initialize and register all searchers
   */
  private initializeSearchers(): void {
    // Register the demo searchers
    searchManager.registerSearcher(webSearcher.getRegistration());
    searchManager.registerSearcher(chatSearcher.getRegistration());
  }

  /**
   * Perform search with debouncing
   */
  public async performSearch(
    query: string,
    setLoading: (loading: boolean) => void,
    setSections: (sections: SearchResultSection[]) => void,
  ): Promise<void> {
    // Clear existing timeout
    if (this.searchTimeout) {
      window.clearTimeout(this.searchTimeout);
    }

    // If query is empty, clear results immediately
    if (!query.trim()) {
      setSections([]);
      setLoading(false);
      return;
    }

    // Set loading state
    setLoading(true);

    // Debounce the search
    this.searchTimeout = window.setTimeout(async () => {
      try {
        const sections = await searchManager.searchAll(query);
        setSections(sections);
      } catch (error) {
        console.error("Search failed:", error);
        setSections([]);
      } finally {
        setLoading(false);
      }
    }, this.SEARCH_DEBOUNCE_MS);
  }

  /**
   * Execute a specific result action
   */
  public executeResult(result: SearchResult): void {
    // Handle different result types
    switch (result.type) {
      case "webpage":
        if (result.url) {
          window.open(result.url, "_blank", "noopener,noreferrer");
        }
        break;
      case "chat-message":
        // In a real implementation, this would navigate to the chat message
        console.log("Navigate to chat message:", result.metadata);
        break;
      default:
        console.log("Execute result:", result);
    }
  }

  /**
   * Get registered searchers
   */
  public getRegisteredSearchers() {
    return searchManager.getSearchers();
  }

  /**
   * Enable/disable a searcher
   */
  public setSearcherEnabled(type: string, enabled: boolean): void {
    searchManager.setSearcherEnabled(type, enabled);
  }

  /**
   * Register a new searcher
   */
  public registerSearcher(registration: SearcherRegistration): void {
    searchManager.registerSearcher(registration);
  }

  /**
   * Unregister a searcher
   */
  public unregisterSearcher(type: string): void {
    searchManager.unregisterSearcher(type);
  }
}

// Singleton instance
export const searchHandler = SearchHandler.getInstance();

